import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft, ShieldAlert, Stamp, Save, Loader2, AlertTriangle, Lock,
    Archive, History, Stethoscope, UserRound, CalendarDays, Info, CheckCircle2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CommonLoader from '../../components/CommonLoader';
import usePermissions from '../../hooks/usePermissions';
import useSession from '../../hooks/useSession';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { hospitalPatientCode } from '../../utils/patientCode';
import { getStudy, saveFormF } from '../../redux/radiologySlice';

const MySwal = withReactContent(Swal);

// FORM F — the PC-PNDT record, and the one screen in this application whose
// subject is a criminal statute rather than a workflow.
//
// WHAT THIS IS. The Pre-Conception and Pre-Natal Diagnostic Techniques Act
// requires that every pre-natal diagnostic procedure — in practice, obstetric and
// pelvic ultrasound on a woman — is recorded on Form F before it is conducted,
// signed by the registered practitioner conducting it, naming the doctor who
// referred her, and KEPT FOR TWO YEARS. Non-compliance is imprisonment and the
// sealing of the machine. It is not a fine, it is not a warning, and it is not
// something a department can catch up on at the end of the week.
//
// SO THIS SCREEN IS A GATE, NOT A REPORT. The console refuses to record the scan
// as performed until this form exists (PNDT_FORM_REQUIRED), and the worklist
// sends people here rather than letting them discover that with a patient on the
// table. Three things make the row a form, and the backend refuses each of them
// by name because "the form is incomplete" sends a radiographer hunting through
// a long screen while somebody waits:
//   - THE DECLARATION, that the person conducting the procedure has explained
//     that determining foetal sex is prohibited and has neither detected nor
//     disclosed it. Without it the row is not an incomplete form; it is not a
//     form.
//   - THE PERFORMING DOCTOR'S REGISTRATION NUMBER. Only a qualified, registered
//     practitioner may conduct the procedure. An unregistered operator is itself
//     the offence, whatever the paperwork says.
//   - THE REFERRING DOCTOR. The chain of accountability — who wanted this scan —
//     is the only thing standing between a lawful anomaly scan and a
//     sex-selective one.
//
// A DOCTOR IS NOT LOCKED OUT OF THIS SCREEN, and that is deliberate rather than
// an oversight in the permission check. A doctor holds no staff permission at all
// in this codebase, and the sonologist conducting an obstetric scan is very often
// a doctor. The route takes the same position (api/routes/radiologyWorkflowRoutes.js):
// a gate that stops them completing the form stops the lawful scan, and a
// department that cannot complete the form under pressure is a department that
// performs without it.
//
// THE FORM IS NEVER REFUSED FOR BEING UNNECESSARY. If the procedure master has
// isPndt unticked, or the patient's sex is not recorded as female, this screen
// still records the declaration and says plainly that the Act does not appear to
// apply. Refusing to write a statutory record because a master flag disagrees is
// the wrong direction — a wrongly-unticked procedure is exactly the case where
// this form is the only evidence the department did the right thing.
//
// BEFORE THE SCAN IT IS EDITED; AFTER IT, IT IS SUPERSEDED. While the study has
// not been performed, no copy of the declaration exists in the world and finishing
// it over two sittings is one act, so saving updates the row. Once the scan has
// happened the row is evidence of a procedure that took place: saving then INSERTS
// a new form and marks the old one superseded, keeping its content untouched. The
// screen says which of the two is about to happen before the button is pressed,
// because "save" meaning two different things without saying so is how somebody
// believes they corrected a record they in fact duplicated.
//
// WHAT IS NOT ON THIS SCREEN. There is no foetal sex field, and there never can
// be one. The mandatory line — "The sex of the foetus has not been detected or
// disclosed" — is printed on the signed report by the PDF renderer, from a flag
// the reporting controller passes explicitly; it is not typed here and not stored
// on the report row.
//
// RETENTION IS STATED IN WORDS, not left as a column. `retainUntil` is stamped
// two years from the DATE OF THE STUDY — never from the day somebody got round to
// typing the form, or a record completed a week late would quietly buy itself an
// extra week of protection, and a facility could not prove it kept the record for
// the period required.
//
// ---------------------------------------------------------------------------
// THE API. redux/radiologySlice.js — getStudy and saveFormF. No URL lives here.
//
// getStudy answers with everything that governs one scan:
//   study: { studyId, accessionNo, status, scheduledAt, performedAt, modality,
//            procedureName, ... }
//   patient: { patientId, name, age, gender, dob }
//   procedure: { procedureId, code, name, modality, bodyPart, isPndt, ... }
//   equipment: { equipmentId, name, room, pndtRegNo, pndtExpiry, ... }
//   equipmentProblem: null | { code, message }
//   order: { orderId, priority, doctorId, doctorName, referrerRegNo, clinicalNotes }
//   pndt: { applies, cleared, form, history: [{ formNo, status, retainUntil }] }
// ---------------------------------------------------------------------------

/** Whole years at a given instant — what the Form asks for, and what the server
 *  falls back to from the date of birth when the field is left empty. */
const yearsBetween = (dob, at) => {
    if (!dob) return '';
    const born = new Date(dob);
    const when = at ? new Date(at) : new Date();
    if (Number.isNaN(born.getTime()) || Number.isNaN(when.getTime())) return '';
    let years = when.getFullYear() - born.getFullYear();
    const monthDelta = when.getMonth() - born.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && when.getDate() < born.getDate())) years -= 1;
    return years >= 0 && years < 130 ? String(years) : '';
};

const dayOnly = (value) => (value ? String(value).slice(0, 10) : '');

const fmtDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Two years from the study's own date, as the server will stamp it. Shown as a
 *  preview so the retention period is on screen BEFORE the form is filed, not
 *  discovered afterwards. */
const retainPreview = (studyDate) => {
    const d = studyDate ? new Date(studyDate) : new Date();
    if (Number.isNaN(d.getTime())) return '';
    const out = new Date(d.getTime());
    out.setFullYear(out.getFullYear() + 2);
    const pad = (n) => String(n).padStart(2, '0');
    return `${out.getFullYear()}-${pad(out.getMonth() + 1)}-${pad(out.getDate())}`;
};

/**
 * Completed weeks of gestation from the last menstrual period, at the date of the
 * scan.
 *
 * OFFERED, NEVER WRITTEN. Gestational age is a clinical finding: a dating scan
 * routinely overrules the menstrual dates, and a remembered LMP is often wrong by
 * a fortnight. Filling the field automatically would put a number the sonologist
 * never agreed to onto a statutory declaration they are about to sign, so this
 * only ever populates a suggestion beside the box.
 */
const weeksFromLmp = (lmp, at) => {
    if (!lmp) return null;
    const start = new Date(lmp);
    const when = at ? new Date(at) : new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(when.getTime())) return null;
    const days = Math.floor((when.getTime() - start.getTime()) / 86400000);
    if (days < 0 || days > 320) return null;
    return Math.round((days / 7) * 10) / 10;
};

const ID_PROOF_TYPES = ['Aadhaar', 'Voter ID', 'PAN', 'Passport', 'Driving licence', 'Ration card', 'Other'];

const EMPTY_FORM = {
    patientName: '',
    patientAge: '',
    husbandOrFatherName: '',
    address: '',
    idProofType: '',
    idProofNumber: '',
    gravida: '',
    parity: '',
    lastMenstrualPeriod: '',
    gestationalAgeWeeks: '',
    indication: '',
    referredBy: '',
    referredByRegNo: '',
    performedByName: '',
    performedByRegNo: '',
    findingsSummary: '',
};

/* ------------------------------------------------------------------ *
 * Small shared field pieces
 * ------------------------------------------------------------------ */

function Field({ label, hint, required, children }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-slate-600">
                {label}
                {required && <span className="text-red-600"> *</span>}
            </span>
            {children}
            {hint && <span className="text-[11px] text-slate-400 mt-1 inline-block">{hint}</span>}
        </label>
    );
}

const inputClass = (invalid) =>
    `mt-1 w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${invalid ? 'border-red-300 bg-red-50/40' : 'border-gray-300'}`;

function SectionCard({ icon, title, subtitle, children, testId }) {
    // Bound to a capitalised local before it is rendered: JSX reads a lowercase
    // tag as an HTML element, so `<icon />` would silently emit an unknown tag
    // and draw nothing at all.
    const Icon = icon;
    return (
        <section data-testid={testId} className="rounded-2xl border border-gray-200 overflow-hidden mb-5">
            <div className="flex items-start gap-2 px-4 py-3 bg-gradient-to-r from-gray-50 to-blue-50/30 border-b border-gray-200">
                <Icon className="w-4 h-4 text-[#2D8A8A] mt-0.5" />
                <div>
                    <h2 className="text-sm font-bold text-slate-700">{title}</h2>
                    {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
            </div>
            <div className="p-4">{children}</div>
        </section>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function PndtFormF() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { canAny } = usePermissions();
    const session = useSession();

    // Route param first, query string second — the same idiom ImagingReporting.jsx
    // uses. The worklist links with a path (/radiology/studies/:studyId/form-f);
    // the query form works on whatever route this page is mounted at, so a link
    // written before the routes are wired cannot send somebody to a URL that does
    // not exist.
    const studyId = params.studyId || searchParams.get('studyId') || '';
    // See the worklist header on why this is passed unconditionally: it is
    // ignored for anybody whose token names a hospital, and it is the only way
    // Super Admin can address one.
    const tenantId = searchParams.get('tenantId') || session.tenantId || '';

    // A doctor holds no staff permission and must not be shut out — see the file
    // header. The server takes exactly this position on the same route.
    const canWrite = canAny([
        'radiology.study.perform',
        'radiology.report.draft',
        'radiology.report.sign',
    ]) || session.isDoctor;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [study, setStudy] = useState(null);
    const [patient, setPatient] = useState(null);
    const [procedure, setProcedure] = useState(null);
    const [equipment, setEquipment] = useState(null);
    const [equipmentProblem, setEquipmentProblem] = useState(null);
    const [order, setOrder] = useState(null);
    const [pndt, setPndt] = useState(null);

    const [form, setForm] = useState(EMPTY_FORM);
    const [declaration, setDeclaration] = useState(false);
    // Which fields have been shown to be wrong, after an attempt to save. Not
    // before: a form that turns red while somebody is still filling in the first
    // box teaches them to ignore red.
    const [touched, setTouched] = useState(false);

    const setField = (key) => (event) => {
        const raw = event?.target?.value ?? '';
        setForm((prev) => ({ ...prev, [key]: typeof raw === 'string' ? raw.replace(/[<>]/g, '') : raw }));
    };

    /* ---- load ---- */
    const load = useCallback(async () => {
        if (!studyId) {
            setError(t('pndtformf.no_study', 'No study was named, and a Form F belongs to one scan.'));
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await dispatch(getStudy({ studyId, tenantId })).unwrap();
            setStudy(data?.study || null);
            setPatient(data?.patient || null);
            setProcedure(data?.procedure || null);
            setEquipment(data?.equipment || null);
            setEquipmentProblem(data?.equipmentProblem || null);
            setOrder(data?.order || null);
            setPndt(data?.pndt || null);
            setError(null);

            const existing = data?.pndt?.form || null;
            const studyDate = data?.study?.performedAt || data?.study?.scheduledAt || null;

            // PREFILLED FROM THE RECORD, NOT LEFT BLANK — but only with what the
            // hospital already knows and can be held to: the patient's own name
            // and age, and the doctor who raised the order with the registration
            // number on their profile. Nothing about the pregnancy is guessed:
            // gravida, parity, the LMP and the indication are asked of the woman
            // and are hers to state.
            setForm({
                ...EMPTY_FORM,
                patientName: existing?.patientName || data?.patient?.name || '',
                patientAge: existing?.patientAge != null
                    ? String(existing.patientAge)
                    : yearsBetween(data?.patient?.dob, studyDate),
                husbandOrFatherName: existing?.husbandOrFatherName || '',
                address: existing?.address || '',
                idProofType: existing?.idProofType || '',
                idProofNumber: existing?.idProofNumber || '',
                gravida: existing?.gravida != null ? String(existing.gravida) : '',
                parity: existing?.parity != null ? String(existing.parity) : '',
                lastMenstrualPeriod: dayOnly(existing?.lastMenstrualPeriod),
                gestationalAgeWeeks: existing?.gestationalAgeWeeks != null ? String(existing.gestationalAgeWeeks) : '',
                indication: existing?.indication || data?.order?.clinicalNotes || '',
                referredBy: existing?.referredBy || data?.order?.doctorName || '',
                referredByRegNo: existing?.referredByRegNo || data?.order?.referrerRegNo || '',
                performedByName: existing?.performedByName || '',
                performedByRegNo: existing?.performedByRegNo || '',
                findingsSummary: existing?.findingsSummary || '',
            });
            // NEVER pre-ticked, even when a completed form is being corrected. The
            // declaration is a personal statement by whoever is conducting THIS
            // procedure; carrying a tick forward from a row somebody else filled
            // in would be this screen making that statement on their behalf.
            setDeclaration(false);
            setTouched(false);
        } catch (err) {
            setError(extractErrorMessage(err, t('pndtformf.load_error', 'Could not load this study')));
        } finally {
            setLoading(false);
        }
    }, [dispatch, studyId, tenantId, t]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const existingForm = pndt?.form || null;
    const applies = Boolean(pndt?.applies);
    const studyDate = study?.performedAt || study?.scheduledAt || null;
    // 'performed' and everything after it. Once a scan has happened its form is
    // evidence, and a correction is a new row rather than an edit.
    const alreadyPerformed = ['performed', 'reported', 'signed', 'released'].includes(String(study?.status));
    const willSupersede = Boolean(existingForm && alreadyPerformed);

    const lmpWeeks = useMemo(
        () => weeksFromLmp(form.lastMenstrualPeriod, studyDate),
        [form.lastMenstrualPeriod, studyDate]
    );

    const missing = {
        referredBy: !form.referredBy.trim(),
        declaration: !declaration,
    };
    const canSubmit = canWrite && !missing.referredBy && !missing.declaration && !saving;

    /* ---- save ---- */
    const handleSave = async () => {
        setTouched(true);
        if (!canWrite || saving) return;

        if (missing.referredBy || missing.declaration) {
            // Refused here as well as at the server, because the two refusals are
            // one round trip apart and the person is standing beside a patient.
            toast.warn(
                missing.declaration
                    ? t('pndtformf.declaration_needed', 'The declaration has to be accepted — without it this is not a form.')
                    : t('pndtformf.referrer_needed', 'Name the referring doctor. The Act asks who wanted this scan.')
            );
            return;
        }

        if (willSupersede) {
            const go = await MySwal.fire({
                title: t('pndtformf.supersede_title', 'This will file a NEW form'),
                text: t(
                    'pndtformf.supersede_text',
                    'The scan has already been performed, so the existing form is evidence of a procedure that took place. Saving files a corrected form and marks the old one superseded — its own content is never rewritten, and both are kept.'
                ),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#2D8A8A',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('pndtformf.supersede_confirm', 'File a corrected form'),
                cancelButtonText: t('pndtformf.cancel', 'Cancel'),
            });
            if (!go.isConfirmed) return;
        }

        setSaving(true);
        try {
            const body = await dispatch(saveFormF({
                studyId,
                tenantId,
                patientName: form.patientName.trim(),
                patientAge: form.patientAge.trim(),
                husbandOrFatherName: form.husbandOrFatherName.trim(),
                address: form.address.trim(),
                idProofType: form.idProofType.trim(),
                idProofNumber: form.idProofNumber.trim(),
                gravida: form.gravida.trim(),
                parity: form.parity.trim(),
                lastMenstrualPeriod: form.lastMenstrualPeriod || '',
                gestationalAgeWeeks: form.gestationalAgeWeeks.trim(),
                indication: form.indication.trim(),
                referredBy: form.referredBy.trim(),
                referredByRegNo: form.referredByRegNo.trim(),
                // Left empty on purpose when the person conducting the procedure
                // is the signed-in user: the server resolves their registration
                // number from the same register the signing gate uses, so the two
                // cannot disagree. Typed in only for a visiting sonologist who is
                // not in this hospital's staff master — a real and lawful case,
                // and a form that could not record them would be filled in under
                // somebody else's name.
                performedByName: form.performedByName.trim(),
                performedByRegNo: form.performedByRegNo.trim(),
                findingsSummary: form.findingsSummary.trim(),
                declarationAccepted: true,
            })).unwrap();

            const saved = body?.data?.form || null;
            const superseded = body?.data?.supersededFormNo || null;
            toast.success(
                superseded
                    ? t('pndtformf.saved_superseding', 'Form {{no}} filed, superseding {{old}}', {
                        no: saved?.formNo || '',
                        old: superseded,
                    })
                    : t('pndtformf.saved', 'Form F recorded{{no}}', { no: saved?.formNo ? ` — ${saved.formNo}` : '' })
            );
            setReloadKey((k) => k + 1);
        } catch (err) {
            // `unwrap()` throws the slice's rejection payload, so the backend's
            // code sits at the top level. Each of these is a different next
            // action, and none of them is fixed by pressing save again.
            const code = err?.code;
            const message = extractErrorMessage(err, t('pndtformf.save_error', 'Could not record the form'));
            if (code === 'PNDT_DOCTOR_NOT_REGISTERED') {
                await MySwal.fire({
                    title: t('pndtformf.not_registered_title', 'No registration number for the doctor conducting this scan'),
                    text: t(
                        'pndtformf.not_registered_text',
                        'Only a registered practitioner may conduct a pre-natal diagnostic procedure, so the form cannot be filed without a registration number. Either an administrator adds it to that person’s record, or — for a visiting sonologist who is not on this hospital’s staff — type their name and number into the two boxes on this form.'
                    ),
                    icon: 'error',
                    confirmButtonColor: '#2D8A8A',
                    confirmButtonText: t('pndtformf.understood', 'Understood'),
                });
                return;
            }
            toast.warn(message);
        } finally {
            setSaving(false);
        }
    };

    /* ---- render ---- */

    if (loading) {
        return (
            <div data-testid="pndt-form-loading" className="bg-white p-6 rounded-2xl shadow-md w-full">
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('pndtformf.loading', 'Loading...')}</span>
                </div>
            </div>
        );
    }

    if (error && !study) {
        return (
            <div data-testid="pndt-form-error" className="bg-white p-6 rounded-2xl shadow-md w-full">
                <button
                    type="button"
                    onClick={() => navigate('/radiology/worklist')}
                    className="inline-flex items-center gap-2 text-sm text-[#2D8A8A] font-semibold cursor-pointer mb-4"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('pndtformf.back', 'Back to the worklist')}
                </button>
                <div className="flex items-center gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            </div>
        );
    }

    const retainUntil = existingForm?.retainUntil || retainPreview(studyDate);

    return (
        <div data-testid="pndt-form-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full pb-16">
            <button
                type="button"
                data-testid="back-to-worklist"
                onClick={() => navigate('/radiology/worklist')}
                className="inline-flex items-center gap-2 text-sm text-[#2D8A8A] font-semibold cursor-pointer w-fit mb-4"
            >
                <ArrowLeft className="w-4 h-4" />
                {t('pndtformf.back', 'Back to the worklist')}
            </button>

            {/* ---- who and what ---- */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-blue-50/30 border border-gray-200 mb-5">
                <div className="flex items-start gap-4">
                    {study?.accessionNo ? (
                        <span
                            data-testid="accession-no"
                            className="inline-flex items-center px-3 py-2 rounded-xl bg-slate-900 text-white shadow-md font-mono text-xl leading-none font-black tracking-wider whitespace-nowrap"
                        >
                            {study.accessionNo}
                        </span>
                    ) : (
                        <span
                            data-testid="accession-no"
                            title={t('pndtformf.not_booked_hint', 'The accession number is minted when the machine is booked')}
                            className="inline-flex items-center px-3 py-2 rounded-xl bg-amber-100 border-2 border-dashed border-amber-400 text-amber-700 text-xs font-bold uppercase tracking-wide whitespace-nowrap"
                        >
                            {t('pndtformf.not_booked', 'Not booked')}
                        </span>
                    )}
                    <div>
                        <h1 data-testid="patient-name" className="text-xl font-bold text-slate-800">
                            {patient?.name || t('pndtformf.unknown_patient', 'Unknown patient')}
                        </h1>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {[patient?.age, patient?.gender, hospitalPatientCode(patient)].filter(Boolean).join(' · ')}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            {[procedure?.name || study?.procedureName, procedure?.bodyPart, equipment?.name, equipment?.room]
                                .filter(Boolean)
                                .join(' · ')}
                        </div>
                        {order?.doctorName && (
                            <div className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
                                <Stethoscope className="w-3 h-3" />
                                {order.doctorName}
                                {order.referrerRegNo ? ` · ${order.referrerRegNo}` : ''}
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-left lg:text-right">
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                        {t('pndtformf.study', 'Study')}
                    </div>
                    {/* The accession number, not the studyId (no system id on screen). */}
                    {study?.accessionNo && <div className="font-mono text-sm text-slate-700 font-semibold">{study.accessionNo}</div>}
                    <div className="text-[11px] text-slate-500 mt-1">
                        {[study?.status, studyDate ? fmtDate(studyDate) : null].filter(Boolean).join(' · ')}
                    </div>
                    {existingForm?.formNo && (
                        <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                            <Stamp className="w-3 h-3" />
                            {existingForm.formNo}
                        </div>
                    )}
                </div>
            </div>

            {/* ---- what this record is, and how long it is kept ---- */}
            <div data-testid="pndt-statute" className="mb-5 flex items-start gap-3 p-4 rounded-2xl bg-red-50 border-2 border-red-300">
                <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                    <div className="font-bold">
                        {t('pndtformf.statute_title', 'PC-PNDT Form F — a statutory record, not a note')}
                    </div>
                    <p className="text-xs text-red-700 mt-1">
                        {t(
                            'pndtformf.statute_body',
                            'This form must exist before a pre-natal diagnostic procedure is conducted. It needs the declaration, the registration number of the doctor conducting it, and the name of the doctor who referred her. Performing the scan without it is a criminal offence — imprisonment and the sealing of the machine, not a fine.'
                        )}
                    </p>
                    <p className="text-xs text-red-900 font-semibold mt-2 inline-flex items-center gap-1.5">
                        <Archive className="w-3.5 h-3.5" />
                        {retainUntil
                            ? t('pndtformf.retention_with_date', 'This record is kept for two years from the date of the scan — until {{date}}.', { date: fmtDate(retainUntil) })
                            : t('pndtformf.retention', 'This record is kept for two years from the date of the scan.')}
                    </p>
                </div>
            </div>

            {/* The Act does not appear to apply — said plainly, and the form is
                still recorded. See the header on why this is not a refusal. */}
            {!applies && (
                <div data-testid="pndt-not-applicable" className="mb-5 flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 text-xs">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                        {t(
                            'pndtformf.not_applicable',
                            'The Act does not appear to apply to this study: the procedure is not marked as a pre-natal diagnostic technique, or the patient is not recorded as female. The form is still recorded if you complete it — a wrongly-configured procedure is exactly the case where this is the only evidence the department did the right thing.'
                        )}
                    </span>
                </div>
            )}

            {/* The machine's own PC-PNDT registration. Operating an unregistered
                console for an obstetric scan is the offence, whatever this form
                says, so the refusal the console would give is shown here too. */}
            {equipmentProblem && (
                <div data-testid="equipment-problem" className="mb-5 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                        <strong className="font-semibold">{t('pndtformf.machine_problem', 'The machine cannot be used for this scan')}: </strong>
                        {equipmentProblem.message}
                    </span>
                </div>
            )}

            {willSupersede && (
                <div data-testid="will-supersede" className="mb-5 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs">
                    <History className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                        {t(
                            'pndtformf.will_supersede',
                            'This scan has already been performed and it already has a form. Saving files a CORRECTED form and marks {{no}} superseded; the old one is kept exactly as it was.',
                            { no: existingForm?.formNo || '' }
                        )}
                    </span>
                </div>
            )}

            {!canWrite && (
                <div data-testid="pndt-readonly" className="mb-5 flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-xs">
                    <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                        {t(
                            'pndtformf.readonly',
                            'You may read this form but not complete it. Filing it needs one of radiology.study.perform, radiology.report.draft or radiology.report.sign — or the doctor conducting the procedure.'
                        )}
                    </span>
                </div>
            )}

            {error && (
                <div className="mb-5 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            <fieldset disabled={!canWrite} className={!canWrite ? 'opacity-70' : ''}>
                {/* ---- the woman ---- */}
                <SectionCard
                    testId="section-woman"
                    icon={UserRound}
                    title={t('pndtformf.section_woman', 'The woman')}
                    subtitle={t('pndtformf.section_woman_hint', 'As she gives it, and as it appears on the identification she produced.')}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label={t('pndtformf.patient_name', 'Name')}>
                            <input
                                data-testid="f-patientName"
                                type="text"
                                maxLength={120}
                                value={form.patientName}
                                onChange={setField('patientName')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field
                            label={t('pndtformf.patient_age', 'Age (completed years)')}
                            hint={t('pndtformf.patient_age_hint', 'Left empty, it is taken from her date of birth on the hospital record.')}
                        >
                            <input
                                data-testid="f-patientAge"
                                type="number"
                                min="0"
                                max="120"
                                value={form.patientAge}
                                onChange={setField('patientAge')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field
                            label={t('pndtformf.husband_father', 'Husband’s or father’s name')}
                            hint={t('pndtformf.husband_father_hint', 'The Form asks for it by name; it is how a record is traced at an inspection.')}
                        >
                            <input
                                data-testid="f-husbandOrFatherName"
                                type="text"
                                maxLength={120}
                                value={form.husbandOrFatherName}
                                onChange={setField('husbandOrFatherName')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field label={t('pndtformf.id_proof_type', 'Identification produced')}>
                            <select
                                data-testid="f-idProofType"
                                value={form.idProofType}
                                onChange={setField('idProofType')}
                                className={`tt-select ${inputClass(false)}`}
                            >
                                <option value="">{t('pndtformf.none_selected', 'Not stated')}</option>
                                {ID_PROOF_TYPES.map((idType) => (
                                    <option key={idType} value={idType}>{idType}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label={t('pndtformf.id_proof_number', 'Identification number')}>
                            <input
                                data-testid="f-idProofNumber"
                                type="text"
                                maxLength={40}
                                value={form.idProofNumber}
                                onChange={setField('idProofNumber')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <div className="sm:col-span-2">
                            <Field
                                label={t('pndtformf.address', 'Address')}
                                hint={t('pndtformf.address_hint', 'A full address, not a locality — this is the record an inspection follows up.')}
                            >
                                <textarea
                                    data-testid="f-address"
                                    rows={2}
                                    maxLength={500}
                                    value={form.address}
                                    onChange={setField('address')}
                                    className={inputClass(false)}
                                />
                            </Field>
                        </div>
                    </div>
                </SectionCard>

                {/* ---- the pregnancy ---- */}
                <SectionCard
                    testId="section-pregnancy"
                    icon={CalendarDays}
                    title={t('pndtformf.section_pregnancy', 'The pregnancy, and why this scan')}
                    subtitle={t('pndtformf.section_pregnancy_hint', 'Asked of the woman. None of it is inferred from the hospital record.')}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field
                            label={t('pndtformf.gravida', 'Gravida')}
                            hint={t('pndtformf.gravida_hint', 'Number of pregnancies including this one.')}
                        >
                            <input
                                data-testid="f-gravida"
                                type="number"
                                min="0"
                                max="30"
                                value={form.gravida}
                                onChange={setField('gravida')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field
                            label={t('pndtformf.parity', 'Parity')}
                            hint={t('pndtformf.parity_hint', 'Number of previous births.')}
                        >
                            <input
                                data-testid="f-parity"
                                type="number"
                                min="0"
                                max="30"
                                value={form.parity}
                                onChange={setField('parity')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field label={t('pndtformf.lmp', 'Last menstrual period')}>
                            <input
                                data-testid="f-lastMenstrualPeriod"
                                type="date"
                                value={form.lastMenstrualPeriod}
                                onChange={setField('lastMenstrualPeriod')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field
                            label={t('pndtformf.gestational_age', 'Gestational age (weeks)')}
                            hint={t(
                                'pndtformf.gestational_age_hint',
                                'A decimal, not “12+3”. Twelve weeks and three days is 12.4.'
                            )}
                        >
                            <input
                                data-testid="f-gestationalAgeWeeks"
                                type="number"
                                step="0.1"
                                min="0"
                                max="45"
                                value={form.gestationalAgeWeeks}
                                onChange={setField('gestationalAgeWeeks')}
                                className={inputClass(false)}
                            />
                            {/* Offered, never written — see weeksFromLmp. */}
                            {lmpWeeks !== null && String(form.gestationalAgeWeeks) !== String(lmpWeeks) && (
                                <button
                                    type="button"
                                    data-testid="use-lmp-weeks"
                                    onClick={() => setForm((prev) => ({ ...prev, gestationalAgeWeeks: String(lmpWeeks) }))}
                                    className="mt-1 text-[11px] text-[#2D8A8A] font-semibold underline underline-offset-2 cursor-pointer"
                                >
                                    {t('pndtformf.use_lmp_weeks', 'By her dates that is {{n}} weeks at the time of the scan — use it', { n: lmpWeeks })}
                                </button>
                            )}
                        </Field>

                        <div className="sm:col-span-2">
                            <Field
                                label={t('pndtformf.indication', 'Indication for the procedure')}
                                hint={t(
                                    'pndtformf.indication_hint',
                                    'The clinical reason this scan is being done — the answer to “why was a pre-natal diagnostic procedure conducted on this woman”.'
                                )}
                            >
                                <textarea
                                    data-testid="f-indication"
                                    rows={2}
                                    maxLength={500}
                                    value={form.indication}
                                    onChange={setField('indication')}
                                    className={inputClass(false)}
                                />
                            </Field>
                        </div>
                    </div>
                </SectionCard>

                {/* ---- the two doctors ---- */}
                <SectionCard
                    testId="section-doctors"
                    icon={Stethoscope}
                    title={t('pndtformf.section_doctors', 'Who asked for it, and who is conducting it')}
                    subtitle={t(
                        'pndtformf.section_doctors_hint',
                        'The chain of accountability. It is the only thing separating a lawful anomaly scan from a sex-selective one.'
                    )}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field
                            label={t('pndtformf.referred_by', 'Referring doctor')}
                            required
                            hint={t('pndtformf.referred_by_hint', 'Prefilled from the order when a doctor raised it. A form without an answer here cannot be filed.')}
                        >
                            <input
                                data-testid="f-referredBy"
                                type="text"
                                maxLength={120}
                                value={form.referredBy}
                                onChange={setField('referredBy')}
                                className={inputClass(touched && missing.referredBy)}
                            />
                            {touched && missing.referredBy && (
                                <span className="text-[11px] text-red-600 mt-1 inline-block">
                                    {t('pndtformf.referrer_needed', 'Name the referring doctor. The Act asks who wanted this scan.')}
                                </span>
                            )}
                        </Field>

                        <Field label={t('pndtformf.referred_by_reg', 'Referring doctor’s registration number')}>
                            <input
                                data-testid="f-referredByRegNo"
                                type="text"
                                maxLength={60}
                                value={form.referredByRegNo}
                                onChange={setField('referredByRegNo')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field
                            label={t('pndtformf.performed_by', 'Doctor conducting the procedure')}
                            hint={t(
                                'pndtformf.performed_by_hint',
                                'Leave both boxes empty if that is you: your own name and registration number are taken from your record. Fill them in for a visiting sonologist who is not on this hospital’s staff.'
                            )}
                        >
                            <input
                                data-testid="f-performedByName"
                                type="text"
                                maxLength={120}
                                value={form.performedByName}
                                onChange={setField('performedByName')}
                                className={inputClass(false)}
                            />
                        </Field>

                        <Field
                            label={t('pndtformf.performed_by_reg', 'Their registration number')}
                            hint={t('pndtformf.performed_by_reg_hint', 'Only a registered practitioner may conduct the procedure. Without a number the form is refused.')}
                        >
                            <input
                                data-testid="f-performedByRegNo"
                                type="text"
                                maxLength={60}
                                value={form.performedByRegNo}
                                onChange={setField('performedByRegNo')}
                                className={inputClass(false)}
                            />
                        </Field>
                    </div>
                </SectionCard>

                {/* ---- what was seen ---- */}
                <SectionCard
                    testId="section-findings"
                    icon={Stamp}
                    title={t('pndtformf.section_findings', 'Summary of findings')}
                    subtitle={t(
                        'pndtformf.section_findings_hint',
                        'The clinical summary the Form asks for. There is no field for foetal sex on this screen and there cannot be one.'
                    )}
                >
                    <textarea
                        data-testid="f-findingsSummary"
                        rows={3}
                        maxLength={1000}
                        value={form.findingsSummary}
                        onChange={setField('findingsSummary')}
                        placeholder={t('pndtformf.findings_placeholder', 'e.g. single live intrauterine foetus, cardiac activity present, liquor adequate')}
                        className={inputClass(false)}
                    />
                </SectionCard>

                {/* ---- the declaration ---- */}
                <section data-testid="section-declaration" className="rounded-2xl border-2 border-slate-900 overflow-hidden mb-5">
                    <div className="px-4 py-3 bg-slate-900 text-white">
                        <h2 className="text-sm font-bold">{t('pndtformf.declaration_title', 'Declaration by the person conducting the procedure')}</h2>
                    </div>
                    <div className="p-4">
                        <label className="flex items-start gap-3">
                            <input
                                data-testid="f-declaration"
                                type="checkbox"
                                checked={declaration}
                                onChange={(e) => setDeclaration(e.target.checked)}
                                className="mt-1 w-5 h-5 accent-[#2D8A8A] cursor-pointer flex-shrink-0"
                            />
                            <span className="text-sm text-slate-700">
                                {t(
                                    'pndtformf.declaration_text',
                                    'I declare that I have explained to the woman that the determination of the sex of the foetus is prohibited by law, and that the sex of the foetus has not been detected or disclosed by me or by anyone conducting this procedure.'
                                )}
                                <span className="block text-[11px] text-slate-500 mt-2">
                                    {t(
                                        'pndtformf.declaration_note',
                                        'That sentence is printed on the signed report as well as kept here. Accepting it records your name, your registration number and the moment you accepted it.'
                                    )}
                                </span>
                            </span>
                        </label>

                        {touched && missing.declaration && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-red-600 font-semibold">
                                <AlertTriangle className="w-4 h-4" />
                                {t('pndtformf.declaration_needed', 'The declaration has to be accepted — without it this is not a form.')}
                            </div>
                        )}

                        {existingForm?.declarationAcceptedAt && (
                            <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                {t('pndtformf.previously_accepted', 'Accepted on the form on file by {{who}} on {{when}}', {
                                    who: existingForm.performedByName || '—',
                                    when: fmtDate(existingForm.declarationAcceptedAt),
                                })}
                            </div>
                        )}
                    </div>
                </section>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <span className="text-xs text-slate-500">
                        {willSupersede
                            ? t('pndtformf.save_hint_supersede', 'Saving files a corrected form and keeps the old one.')
                            : existingForm
                                ? t('pndtformf.save_hint_edit', 'The scan has not been recorded yet, so this updates the form already started.')
                                : t('pndtformf.save_hint_new', 'The scan cannot be recorded as performed until this is filed.')}
                    </span>
                    <button
                        type="button"
                        data-testid="save-form-f"
                        disabled={!canSubmit}
                        onClick={handleSave}
                        className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white text-sm font-bold shadow-lg ${canSubmit ? 'cursor-pointer hover:shadow-xl' : 'opacity-60 cursor-not-allowed'}`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {willSupersede
                            ? t('pndtformf.save_superseding', 'File a corrected Form F')
                            : t('pndtformf.save', 'File Form F')}
                    </button>
                </div>
            </fieldset>

            {/* ---- every form ever written for this scan ----
                Superseded ones included. "Why are there two forms for this scan"
                is a question an inspection asks, and the department has to be
                able to answer it from the screen rather than from the database. */}
            {Array.isArray(pndt?.history) && pndt.history.length > 0 && (
                <section data-testid="form-history" className="mt-6 rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-gray-200">
                        <History className="w-4 h-4 text-slate-500" />
                        <h2 className="text-sm font-bold text-slate-700">
                            {t('pndtformf.history_title', 'Forms filed for this scan')}
                        </h2>
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {pndt.history.map((row) => (
                            <li key={row.formId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs">
                                <span className="font-mono font-semibold text-slate-700">{row.formNo || '—'}</span>
                                <span
                                    className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${row.status === 'complete' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}
                                >
                                    {row.status}
                                </span>
                                <span className="text-slate-500">
                                    {row.declarationAcceptedAt
                                        ? t('pndtformf.history_accepted', 'Declared {{when}}', { when: fmtDate(row.declarationAcceptedAt) })
                                        : ''}
                                </span>
                                <span className="text-slate-500 inline-flex items-center gap-1">
                                    <Archive className="w-3 h-3" />
                                    {row.retainUntil
                                        ? t('pndtformf.history_retain', 'Kept until {{when}}', { when: fmtDate(row.retainUntil) })
                                        : ''}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
